import React, { ReactNode } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { operationChartSelectSchema } from '../../schemas/form.schemas'
import {
  Table as ChakraTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  chakra,
  Flex,
  Box,
  Text,
  InputGroup,
  InputLeftElement,
  Input,
  Stack,
  Container,
  ButtonGroup,
  Button,
  Spinner,
} from '@chakra-ui/react'
import { ArrowDownIcon, ArrowUpIcon } from '@chakra-ui/icons'

import {
  useTable,
  useSortBy,
  usePagination,
  Column,
  useGlobalFilter,
  useAsyncDebounce,
} from 'react-table'

import { CSVLink } from 'react-csv'

import { FiSearch } from 'react-icons/fi'
import { getCSVData } from '../../helpers/functions/CSV/getCSVData'
import { OperationChartSelectForm, SelectProps } from '../../@types/forms/forms.types'
import { ChartSelect } from '../ChartSelect'
import { DateTime } from 'luxon'
// import { get } from 'lodash'

const monthsArray = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
]

const optionsSelect = [
  {
    label: 'Janeiro',
    value: 'jan',
  },
  {
    label: 'Fevereiro',
    value: 'feb',
  },
  {
    label: 'Março',
    value: 'mar',
  },
  {
    label: 'Abril',
    value: 'apr',
  },
  {
    label: 'Maio',
    value: 'may',
  },
  {
    label: 'Junho',
    value: 'jun',
  },
  {
    label: 'Julho',
    value: 'jul',
  },
  {
    label: 'Agosto',
    value: 'aug',
  },
  {
    label: 'Setembro',
    value: 'sep',
  },
  {
    label: 'Outubro',
    value: 'oct',
  },
  {
    label: 'Novembro',
    value: 'nov',
  },
  {
    label: 'Dezembro',
    value: 'dec',
  },
]

type TableProps = {
  columns: Column[]
  data: any[]
  title: string
  download?: boolean
  addNew?: ReactNode
  filter?: string
  placeholder?: string
  isLoading?: boolean
  isRefetching?: boolean
  table_name:
    | 'financial'
    | 'rating'
    | 'monthly'
    | 'management'
    | 'configurationAdmin'
    | 'accredited'
    | 'users'
    | 'coupons'
}

const handleWatched = (watched : string) => {
  let indexMonth = monthsArray.indexOf(watched);
  indexMonth += 1;
  let stringMonthIndex : string
  if (indexMonth < 10) {
    stringMonthIndex = `0${indexMonth}`
  } else {
    stringMonthIndex = indexMonth.toString()
  }
  return `-${stringMonthIndex}-`
}

export function Table({
  columns,
  data,
  title,
  download = false,
  filter,
  table_name,
  addNew,
  placeholder,
  isLoading,
  isRefetching,
}: TableProps) {

  const {
    getTableProps,
    globalFilteredRows,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setGlobalFilter,
    state: { pageIndex, globalFilter },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageSize: 10, pageIndex: 0, hiddenColumns: ['street'] },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  )

  const [filterInput, setFilterInput] = React.useState(globalFilter)

  const onFilterChange = useAsyncDebounce(value => {
    setGlobalFilter(value || undefined)
  }, 200)

  const {
    control,
  } = useForm<OperationChartSelectForm>({
    resolver: zodResolver(operationChartSelectSchema),
  })

  const watched = useWatch({
    control,
    name: 'month.value',
    defaultValue: DateTime.utc().setLocale('en-us').monthLong.slice(0, 3).toLowerCase(),
  })

  if (table_name === 'financial') {
    watched && onFilterChange(handleWatched(watched))
  }

  const csvData = table_name === "financial"
    || table_name === "accredited"
    || table_name === "monthly"
    ? getCSVData(table_name, columns, globalFilteredRows)
    : [[null]]

  return (
    <Container
      width="100%"
      marginX="auto"
      maxWidth="100vw"
      paddingX={{ base: '0', notebook: '2rem' }}
      paddingY={{ base: '0', notebook: '2rem' }}
    >
      <Box marginTop={{ base: "1.25rem", notebook: 0 }} marginX="0">
        <Stack
          display="flex"
          direction="column"
        >
          <Box paddingX={{ base: '1rem', notebook: '1.5rem' }}>
            <Stack
              direction={{ base: 'column', tablet: 'row' }}
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing="1.5rem"
                justifyContent={{ base: 'center', notebook: 'unset' }}
              >
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  color="parkoff.orange.300"
                >
                  {title}
                </Text>
                {!isLoading && isRefetching && (
                  <Spinner
                    speed="0.65s"
                    emptyColor="gray.200"
                    color="red.500"
                  />
                )}
              </Stack>

              { (table_name === "financial")
              ? (
                <ChartSelect<OperationChartSelectForm, SelectProps, false>
                  name="month"
                  control={control}
                  size="lg"
                  defaultValue={DateTime.utc().setLocale('en-us').monthLong.slice(0, 3).toLowerCase()}
                  label="Filtrar por:"
                  options={optionsSelect}
                  placeholder="Mês"
                  closeMenuOnSelect={true}
                />
              )
              : (
                <InputGroup
                  display="flex"
                  alignItems="center"
                  width="100%"
                  position="relative"
                  maxWidth={{ base: 'unset', notebook: '16rem' }}
                  marginTop={{ base: '0.5rem', notebook: '0' }}
                  marginInlineStart={{ base: '0', notebook: '0.5rem' }}
                  color="gray.600"
                >
                  <InputLeftElement
                    display="flex"
                    alignItems="center"
                    top="unset !important"
                  >
                    <FiSearch
                      display="inline-block"
                      width="1.25rem"
                      height="1.25rem"
                      color="gray.600"
                    />
                  </InputLeftElement>
                  <Input
                    value={filterInput}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setFilterInput(event.target.value)
                      onFilterChange(event.target.value)
                    }}
                    placeholder={placeholder}
                    _placeholder={{
                      color: 'gray.600',
                    }}
                  />
                </InputGroup>
              )
              }
              {/* <InputGroup
                display="flex"
                alignItems="center"
                width="100%"
                position="relative"
                maxWidth={{ base: 'unset', notebook: '16rem' }}
                marginTop={{ base: '0.5rem', notebook: '0' }}
                marginInlineStart={{ base: '0', notebook: '0.5rem' }}
                color="gray.600"
              >
                <InputLeftElement
                  display="flex"
                  alignItems="center"
                  top="unset !important"
                >
                  <FiSearch
                    display="inline-block"
                    width="1.25rem"
                    height="1.25rem"
                    color="gray.600"
                  />
                </InputLeftElement>
                <Input
                  value={filterInput}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFilterInput(event.target.value)
                    onFilterChange(event.target.value)
                  }}
                  placeholder={placeholder}
                  _placeholder={{
                    color: 'gray.600',
                  }}
                />
              </InputGroup> */}
              
              { (table_name === "financial"
              || table_name === "accredited"
              || table_name === "monthly")
              && (
                <Flex
                  fontSize="12px"
                  fontWeight="700"
                  height={{base: "2.5rem", tablet: "2rem" }}
                  width={{base: "100%", tablet: "auto" }}
                  alignItems="center"
                  justifyContent="center"
                  background="none"
                  border="1px solid"
                  borderColor="orange.300"
                  borderRadius="10px"
                  textColor="gray.800"
                  minWidth="10rem"
                >
                  <CSVLink className="csv-download" filename="dados.csv" data={csvData}>
                    <Text>Exportar</Text>
                  </CSVLink>
                </Flex>
              )
              }
              {addNew && addNew}
            </Stack>
          </Box>
        </Stack>
      </Box>
      <Flex
        overflowX="auto"
        marginTop="1.25rem"
        marginBottom="0"
        marginX="auto"
      >
        <ChakraTable {...getTableProps()}>
          <Thead background="#F7FAFC">
            {headerGroups.map(headerGroup => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <Th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    fontSize="sm"
                    fontWeight="normal"
                    textTransform="none"
                  >
                    {column.render('Header')}
                    <chakra.span pl="4">
                      {column.isSorted &&
                        (column.isSortedDesc ? (
                          <ArrowDownIcon />
                        ) : (
                          <ArrowUpIcon />
                        ))}
                    </chakra.span>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {page.map(row => {
              prepareRow(row)
              return (
                <Tr {...row.getRowProps()}>
                  {row.cells.map(cell => (
                    <Td
                      {...cell.getCellProps({
                        style: {
                          background: cell.row.index % 2 ? 'white' : '#F4F4F4',
                        },
                      })}
                      fontSize="sm"
                    >
                      {cell.render('Cell')}
                    </Td>
                  ))}
                </Tr>
              )
            })}
          </Tbody>
        </ChakraTable>
      </Flex>

      <Box
        marginTop="2rem"
        marginX="0"
        paddingX={{ base: '1rem', notebook: '1.5rem' }}
        paddingBottom="1.25rem"
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Text
            fontSize="sm"
            color="gray.600"
            display={{ base: 'none', tablet: 'block' }}
          >
            Página {pageIndex + 1} de {pageOptions.length}
          </Text>
          <ButtonGroup
            display="inline-flex"
            width={{ base: 'full', tablet: 'auto' }}
            justifyContent="space-between"
          >
            <Button
              fontSize="sm"
              fontWeight="semibold"
              borderWidth="1px"
              borderColor="gray.300"
              background="white"
              _hover={{
                background: 'gray.100',
              }}
              disabled={!canPreviousPage}
              onClick={() => previousPage()}
            >
              Anterior
            </Button>
            <Button
              fontSize="sm"
              fontWeight="semibold"
              borderWidth="1px"
              borderColor="gray.300"
              background="white"
              _hover={{
                background: 'gray.100',
              }}
              disabled={!canNextPage}
              onClick={() => nextPage()}
            >
              Próxima
            </Button>
            {/* <Button onClick={handleClick}>
              Clica
            </Button> */}
          </ButtonGroup>
        </Stack>
      </Box>
    </Container>
  )
}
